import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  List,
  Typography,
  Button,
  Badge,
  
  Row,
  Col,
  Pagination,
  Empty,
  Spin,
  Space,
  Divider,
  Tooltip,
  message
} from 'antd';
import {
  BellOutlined,
  CheckOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  fetchUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from '../../services/notificationService';

const { Title, Text } = Typography;

const AllNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch notifications
  const fetchNotifications = useCallback(async (page = 1, search = '', type = 'all', status = 'all') => {
    setLoading(true);
    try {
      // Pass individual arguments as expected by the service function
      const response = await dispatch(fetchUserNotifications(page, pageSize, search, type, status));
      setNotifications(response.data || []);
      setTotalNotifications(response.total || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      message.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [dispatch, pageSize]);

  useEffect(() => {
    fetchNotifications(currentPage, searchTerm, filterType, filterStatus);
  }, [currentPage, searchTerm, filterType, filterStatus, fetchNotifications]);

  

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Mark notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await dispatch(markNotificationAsRead(notificationId));
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, is_read: '1' } : notif
        )
      );
      message.success('Notification marked as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      message.error('Failed to mark notification as read');
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    setBulkActionLoading(true);
    try {
      await dispatch(markAllNotificationsAsRead());
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, is_read: '1' }))
      );
      message.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      message.error('Failed to mark all notifications as read');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (notification.is_read === '0') {
      handleMarkAsRead(notification.id);
    }

    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  // Get notification type icon
  const getNotificationIcon = (type) => {
    const iconMap = {
      'success': <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      'error': <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      'warning': <WarningOutlined style={{ color: '#faad14' }} />,
      'info': <InfoCircleOutlined style={{ color: '#1890ff' }} />
    };
    return iconMap[type] || <BellOutlined style={{ color: '#8c8c8c' }} />;
  };

  // Format time ago
  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
    return date.toLocaleDateString();
  };

  // Get unread count
  const unreadCount = notifications.filter(n => n.is_read === '0').length;

  return (
    <div className="container-xxl py-4">
      <Card>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                <BellOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
                Notifications
                {unreadCount > 0 && (
                  <Badge
                    count={unreadCount}
                    style={{ marginLeft: '12px' }}
                    showZero={false}
                  />
                )}
              </Title>
            </Col>
            <Col>
              <Space>
                {unreadCount > 0 && (
                  <Button
                    icon={<CheckOutlined />}
                    onClick={handleMarkAllAsRead}
                    loading={bulkActionLoading}
                  >
                    Mark All Read
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        </div>

        <Divider />

        {/* Notifications List */}
        <Spin spinning={loading}>
          {notifications.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Text type="secondary">No notifications found</Text>
                  {(searchTerm || filterType !== 'all' || filterStatus !== 'all') && (
                    <div style={{ marginTop: '8px' }}>
                      <Button
                        type="link"
                        onClick={() => {
                          setSearchTerm('');
                          setFilterType('all');
                          setFilterStatus('all');
                          setCurrentPage(1);
                        }}
                      >
                        Clear filters
                      </Button>
                    </div>
                  )}
                </div>
              }
              style={{ margin: '60px 0' }}
            />
          ) : (
            <List
              dataSource={notifications}
              renderItem={(item) => (
                <List.Item
                  style={{
                    padding: '20px',
                    cursor: item.action_url ? 'pointer' : 'default',
                    backgroundColor: item.is_read === '0' ? '#f6ffed' : '#ffffff',
                    border: item.is_read === '0' ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    transition: 'all 0.2s ease',
                    position: 'relative'
                  }}
                  onClick={() => item.action_url && handleNotificationClick(item)}
                  onMouseEnter={(e) => {
                    if (item.action_url) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (item.action_url) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                  actions={[
                    <Space key="actions">
                      {item.is_read === '0' && (
                        <Tooltip title="Mark as read">
                          <Button
                            type="text"
                            size="small"
                            icon={<CheckOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(item.id);
                            }}
                          />
                        </Tooltip>
                      )}
                    </Space>
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {getNotificationIcon(item.type)}
                        {item.is_read === '0' && (
                          <div
                            style={{
                              width: '6px',
                              height: '6px',
                              backgroundColor: '#52c41a',
                              borderRadius: '50%',
                              marginLeft: '8px'
                            }}
                          />
                        )}
                      </div>
                    }
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Text
                          strong={item.is_read === '0'}
                          style={{
                            fontSize: '16px',
                            color: '#262626',
                            lineHeight: '1.4'
                          }}
                        >
                          {item.title}
                        </Text>
                        <Text
                          type="secondary"
                          style={{
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            marginLeft: '16px'
                          }}
                        >
                          {timeAgo(item.created_at)}
                        </Text>
                      </div>
                    }
                    description={
                      <div style={{ marginTop: '8px' }}>
                        <Text
                          style={{
                            fontSize: '14px',
                            color: '#595959',
                            lineHeight: '1.5'
                          }}
                        >
                    <span dangerouslySetInnerHTML={{ __html: item.message }} />
                        </Text>
                        {item.action_url && (
                          <div style={{ marginTop: '8px' }}>
                            <Badge status="processing" text="Click to view details" />
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Spin>

        {/* Pagination */}
        {totalNotifications > pageSize && (
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <Pagination
              current={currentPage}
              total={totalNotifications}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
              showQuickJumper
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} notifications`
              }
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default AllNotifications;